import {useProducts} from '@api'
import {activeCell, getUserData, isCompanyUser, isSuperAdmin, renderClickableCell, useViewNavigate} from '@common'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef, GridColumnVisibilityModel} from '@mui/x-data-grid'
import {pageLinksAtom, getCompanyCurrencyAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {formatCurrency} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface ProductTableData {
  name: string
  accessories: number
  price: string
  active: boolean
}

export function usePRoductSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data} = useProducts()

  const rows = useMemo(() => {
    if (!data) return []
    else
      return data
        .filter(item => (isSuperAdmin() ? item.companyId === userSelection?.companyId : true))
        .map(item => ({
          name: item.name,
          accessories: item.accessories.length,
          price: formatCurrency(item.price, currency),
          active: item.active,
          id: item.id,
        }))
  }, [data])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: TranslationKey['Products and accessories'], link: NavigationPathKey.PRODUCT_DASHBOARD},
        {title: TranslationKey['Product Search']},
      ]),
    [],
  )

  /**
   * Product table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1.2,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.PRODUCT_VIEW}/${params.row.id}`)),
    },
    {field: 'accessories', headerName: t(TranslationKey['No. of accessories']), flex: 0.5},
    {field: 'price', headerName: t(TranslationKey['Default price']), flex: 0.5, headerAlign: 'right', align: 'right'},
    activeCell(t),
  ]

  /**
   * Product table visibility model configuration
   */
  const visibilityModel: GridColumnVisibilityModel = {
    company: false,
    accessories: false,
  }

  /**
   * Create button should only be available for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {navigate, t, visibilityModel, showCreateButton, rows, columns}
}
